<script lang="ts">
    import { Component, Emit, mixins, Prop, Watch } from "vue-facing-decorator";
    import ButtonComponent from "@/components/atoms/button/Button.vue";
    import ActionButton from "@/components/atoms/action-button/ActionButton.vue";
    import PriceLevelFilter from "@/components/ticket-selection/seatmap/PriceLevelFilter.vue";
    import { BreakpointHandler, TicketSelectionDisplay } from "@/mixins";
    import StepperComponent from "@/components/atoms/stepper/Stepper.vue";
    import { StepperState } from "@/components/atoms/stepper/stepper-state.interface";
    import {
        controlStateService,
        panelStateService,
        usePromotionStore,
        useSeatmapStore
    } from "@/domain/ticket-selection";
    import { IconName, PerformanceQASelectors, TranslationKey } from "webshop-modules";

    @Component({
        components: {StepperComponent, PriceLevelFilter, ActionButton, ButtonComponent}
    })
    export default class DesktopControls extends mixins(BreakpointHandler) {

        @Prop()
        public data!: TicketSelectionDisplay;

        @Prop()
        public isPricelevelSelected!: boolean;

        public readonly IconName: typeof IconName = IconName;
        public readonly Translation: typeof TranslationKey = TranslationKey;
        public readonly PerformanceQASelectors: typeof PerformanceQASelectors = PerformanceQASelectors;
        public readonly panelState = panelStateService;
        public readonly controlState = controlStateService;
        public readonly seatmapStore = useSeatmapStore();
        public readonly promotionStore = usePromotionStore();

        public exitLevelLabel = '';

        @Emit()
        public zoomLevelChange(data: StepperState): StepperState {
            return data;
        }

        @Emit()
        public layerExit(): boolean {
            return true;
        }

        @Watch('panelState.actions.selectFilter')
        private onSelectedFilterChange() {
            this.controlState.resetState();
        }

        @Watch('promotionStore.active')
        private onPromotionChange() {
            this.controlState.resetState();
        }

        public toggleSidebarPromotionPanel() {
            if (this.panelState.isPromotionPanelActive()) {
                this.panelState.closePanel();
            } else {
                this.panelState.activatePromotionPanel();
            }
        }

        private async created() {
            this.exitLevelLabel = await this.$translationService
                .getTranslationFor(this.Translation.Navigate_back_label);
        }

    }
</script>

<template>
    <div class="m-desktopControls__container"
         data-js="seatmap-controls"
         v-show="seatmapStore.isReady && !seatmapStore.hasGenericError"
         :data-qa="PerformanceQASelectors.SEATMAP_BUTTONS">

        <div class="m-desktopControls__topRow">
            <div class="m-desktopControls__actionButtons">
                <div v-if="seatmapStore.priceLevelFilter.length"
                     v-bind:class="{'-opened' : controlState.isFilterOpen, '-active': isPricelevelSelected}"
                     class="m-desktopControls__filterButton">
                    <ActionButton :label="{key: Translation.Seatmap_action_filter}"
                                  :icon-name="controlState.isFilterOpen ? IconName.Close : IconName.SmallFilter"
                                  v-on:clicked="controlState.switchFilterDialogDisplayTo(!controlState.isFilterOpen)">
                    </ActionButton>
                </div>
                <div v-if="promotionStore.availablePromotions.length > 0"
                     v-bind:class="{ '-active': promotionStore.active }"
                     class="m-desktopControls__promoButton">
                    <ActionButton :label="{key: Translation.Seatmap_action_promo}"
                                  :icon-name="IconName.Percentage"
                                  v-on:clicked="toggleSidebarPromotionPanel()"></ActionButton>
                </div>
                <template v-if="controlState.isFilterOpen">
                    <div v-on:click="controlState.switchFilterDialogDisplayTo(false)"
                         class="m-desktopControls__filterBackdrop"></div>
                    <div class="m-desktopControls__filterContainer">
                        <price-level-filter :data="data"></price-level-filter>
                    </div>
                </template>
            </div>

            <template v-if="!isMobile">
                <div class="m-desktopControls__zoom">
                    <stepper-component
                        :state="seatmapStore.zoom"
                        :show-amount="false"
                        v-on:state-change="zoomLevelChange">
                    </stepper-component>
                </div>
            </template>
        </div>

        <div data-js="seatmap-back-action"
             v-bind:style="{
                    display: controlState.displayExitLayerButton ? '' : 'none',
                    top: isMobile ? '15px' : '60px',
                 }"
             class="m-desktopControls__backButton">
            <ActionButton :icon-name="IconName.Arrow" :label="exitLevelLabel" v-on:clicked="layerExit"></ActionButton>
        </div>
    </div>
</template>